import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { IdeaDetailResponse, IdeaStatus } from '@shared/types';
import { IDEA_STATUS_LABELS, AI_RUN_STATUS_LABELS } from '@shared/ideaStatus';
import { formatDateTime, relativeTime } from '@shared/format';
import { PageContainer } from '../../app/AppShell';
import { useAuthStore } from '../../app/authStore';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, CardBody, CardHeader, CardTitle, EmptyState, Spinner } from '../../components/Card';
import { TabBar } from '../../components/Tabs';
import { toast } from '../../components/Toast';
import { Modal } from '../../overlay';
import { api, errorMessage } from '../../lib/api';
import { usePolling } from '../../hooks/usePolling';
import { useSettingsStore } from '../admin/settingsStore';
import { useIdeasStore } from './ideasStore';
import { AiReportPanel } from './components/AiReportPanel';
import { CommentThread } from './components/CommentThread';
import { EventFeed } from './components/EventFeed';
import { IdeaForm, type IdeaFormValue } from './components/IdeaForm';
import { IdeaStatusFlow, type MemberOption } from './components/IdeaStatusFlow';
import { AiStateBadge, IdeaStatusBadge, TagList } from './components/ideaBadges';

/** 轮询上限。模型通常几十秒出结果，超过这个时间说明卡住了，提示用户重试。 */
const POLL_INTERVAL_MS = 2500;
const RUN_TIMEOUT_MS = 3 * 60 * 1000;

const AI_RUNNABLE_STATUSES: readonly IdeaStatus[] = ['submitted', 'validated'];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-1.5">
      <h3 className="text-xs font-semibold text-fg-muted">{title}</h3>
      <div className="idea-body text-xs leading-relaxed text-fg">{children}</div>
    </section>
  );
}

export function IdeaDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const settings = useSettingsStore((state) => state.settings);
  const loadSettings = useSettingsStore((state) => state.load);
  const upsert = useIdeasStore((state) => state.upsert);

  const [idea, setIdea] = useState<IdeaDetailResponse | null>(null);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'discussion' | 'events'>('discussion');
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [runPending, setRunPending] = useState(false);
  const [deadline, setDeadline] = useState<number | null>(null);

  const fetchDetail = useCallback(async () => {
    try {
      const detail = await api.get<IdeaDetailResponse>(`/api/ideas/${id}`);
      setIdea(detail);
      upsert(detail);
      setError(null);
    } catch (fetchError) {
      setError(errorMessage(fetchError));
    } finally {
      setLoading(false);
    }
  }, [id, upsert]);

  useEffect(() => {
    setLoading(true);
    void fetchDetail();
    void loadSettings();
    void api
      .get<{ items: MemberOption[] }>('/api/members')
      .then((result) => setMembers(result.items))
      .catch(() => setMembers([]));
  }, [fetchDetail, loadSettings]);

  const runStatus = idea?.latestRun?.status ?? null;
  const runActive = runPending || runStatus === 'queued' || runStatus === 'running';

  // 一旦有研判在跑就开始计时，超过上限停止轮询并提示重试 ——
  // 否则用户会对着一个永远转圈的界面等下去。
  useEffect(() => {
    if (!runActive) {
      setDeadline(null);
      return;
    }
    setDeadline((current) => current ?? Date.now() + RUN_TIMEOUT_MS);
  }, [runActive]);

  const expired = deadline !== null && Date.now() > deadline;
  usePolling(fetchDetail, POLL_INTERVAL_MS, runActive && !expired);

  const startRun = async () => {
    if (!idea) return;
    setRunPending(true);
    try {
      await api.post(`/api/ideas/${idea.id}/ai-runs`);
      toast.ok('已发起研判', '模型出结论通常需要几十秒，页面会自动刷新。');
    } catch (runError) {
      toast.critical('发起研判失败', errorMessage(runError));
    } finally {
      setRunPending(false);
      setDeadline(Date.now() + RUN_TIMEOUT_MS);
      void fetchDetail();
    }
  };

  const toggleReaction = async (kind: 'like' | 'follow') => {
    if (!idea) return;
    try {
      const result = await api.post<{ kind: string; active: boolean; count: number }>(`/api/ideas/${idea.id}/reactions`, { kind });
      setIdea((current) =>
        current === null
          ? current
          : {
              ...current,
              likeCount: kind === 'like' ? result.count : current.likeCount,
              viewer: {
                ...current.viewer,
                liked: kind === 'like' ? result.active : current.viewer.liked,
                followed: kind === 'follow' ? result.active : current.viewer.followed,
              },
            },
      );
    } catch (reactionError) {
      toast.critical('操作失败', errorMessage(reactionError));
    }
  };

  const saveEdit = async (value: IdeaFormValue) => {
    if (!idea) return;
    setSavingEdit(true);
    try {
      const updated = await api.patch<IdeaDetailResponse>(`/api/ideas/${idea.id}`, value);
      setIdea(updated);
      setEditing(false);
      toast.ok('已保存', '正文改动后原研判结论可能过时，建议重新研判。');
    } catch (saveError) {
      toast.critical('保存失败', errorMessage(saveError));
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Spinner /> 正在加载创意…
        </div>
      </PageContainer>
    );
  }

  if (!idea) {
    return (
      <PageContainer>
        <Card>
          <CardBody>
            <EmptyState
              title="创意不存在或你没有访问权限"
              description={error ?? '它可能已被删除，或者还是一份别人的草稿。'}
              action={<Button onClick={() => navigate('/board')}>返回看板</Button>}
            />
          </CardBody>
        </Card>
      </PageContainer>
    );
  }

  const canRun = permissions.includes('ai.run') && AI_RUNNABLE_STATUSES.includes(idea.status);
  const canDeleteAny = permissions.includes('comment.delete.any');

  return (
    <PageContainer>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <IdeaStatusBadge status={idea.status} />
            <AiStateBadge state={idea.aiState} />
            <span className="text-[11px] text-fg-subtle">
              {idea.authorName} 提交于 {formatDateTime(idea.createdAt)} · 更新于 {relativeTime(idea.updatedAt)}
            </span>
          </div>
          <h1 className="mt-1.5 text-xl font-semibold text-fg">{idea.title}</h1>
          {idea.summary ? <p className="mt-1 max-w-3xl text-sm leading-relaxed text-fg-muted">{idea.summary}</p> : null}
          <div className="mt-2">
            <TagList tags={idea.tags} />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant={idea.viewer.liked ? 'primary' : 'default'} onClick={() => void toggleReaction('like')}>
            {idea.viewer.liked ? '已赞' : '点赞'} {idea.likeCount}
          </Button>
          <Button size="sm" variant={idea.viewer.followed ? 'primary' : 'default'} onClick={() => void toggleReaction('follow')} title="关注后出现在「我参与的」里">
            {idea.viewer.followed ? '已关注' : '关注'}
          </Button>
          {idea.canEdit ? (
            <Button size="sm" variant="default" onClick={() => setEditing(true)}>
              编辑
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => navigate('/board')}>
            返回看板
          </Button>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="flex min-w-0 flex-col gap-4">
          <Card>
            <CardBody>
              <IdeaStatusFlow idea={idea} members={members} onUpdated={setIdea} />
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle subtitle="这些信息是 AI 研判的输入，缺一项结论就会打折">创意内容</CardTitle>
            </CardHeader>
            <CardBody className="flex flex-col gap-4">
              {idea.summary ? <Section title="一句话说明">{idea.summary}</Section> : null}
              <Section title="详细说明">{idea.body || '（未填写）'}</Section>
              <div className="grid gap-4 md:grid-cols-3">
                <Section title="核心假设">{idea.hypothesis || '（未填写）'}</Section>
                <Section title="面向谁">{idea.audience || '（未填写）'}</Section>
                <Section title="成功指标">{idea.successMetric || '（未填写）'}</Section>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <TabBar
                size="md"
                value={tab}
                onChange={(value) => setTab(value as 'discussion' | 'events')}
                items={[
                  { key: 'discussion', label: '讨论', badge: idea.comments.length || undefined },
                  { key: 'events', label: '创意动态', badge: idea.events.length || undefined },
                ]}
              />
            </CardHeader>
            <CardBody>
              {tab === 'discussion' ? (
                <CommentThread
                  ideaId={idea.id}
                  comments={idea.comments}
                  currentUserId={user?.id ?? ''}
                  canDeleteAny={canDeleteAny}
                  onAdded={(comment) => setIdea((current) => (current ? { ...current, comments: [...current.comments, comment] } : current))}
                  onDeleted={(commentId) =>
                    setIdea((current) => (current ? { ...current, comments: current.comments.filter((item) => item.id !== commentId) } : current))
                  }
                />
              ) : (
                <EventFeed events={idea.events} />
              )}
            </CardBody>
          </Card>
        </div>

        <div className="flex min-w-0 flex-col gap-4">
          <AiReportPanel
            idea={idea}
            canRun={canRun}
            aiEnabled={settings.aiEnabled}
            aiConfigured={settings.aiConfigured}
            running={runPending}
            onRun={() => void startRun()}
          />

          {expired ? (
            <p className="rounded-md border border-warning/50 bg-warning-soft px-3 py-2 text-[11px] leading-relaxed text-warning">
              研判已超过 3 分钟仍未返回，页面停止自动刷新。请点「重新研判」重试；若反复失败，检查 Worker 的 AI 配置与出网情况。
            </p>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle subtitle="重新研判不会覆盖旧结论，历史全部保留">研判记录</CardTitle>
            </CardHeader>
            <CardBody>
              {idea.runs.length === 0 ? (
                <p className="text-[11px] text-fg-subtle">还没有发起过研判。</p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {idea.runs.map((run) => (
                    <li key={run.id} className="flex flex-col gap-0.5 border-b border-line pb-2 text-[11px] last:border-b-0 last:pb-0">
                      <div className="flex items-center gap-2">
                        <Badge tone={run.status === 'succeeded' ? 'ok' : run.status === 'failed' ? 'critical' : 'info'}>
                          {AI_RUN_STATUS_LABELS[run.status] ?? run.status}
                        </Badge>
                        <span className="text-fg-muted">{run.triggeredByName}</span>
                        <span className="ml-auto text-fg-subtle" title={run.createdAt}>
                          {relativeTime(run.createdAt)}
                        </span>
                      </div>
                      <span className="text-fg-subtle">
                        {run.model || '未指定模型'}
                        {run.latencyMs !== null ? ` · 耗时 ${(run.latencyMs / 1000).toFixed(1)}s` : ''}
                        {run.reasoningTokens !== null ? ` · 其中 ${run.reasoningTokens} 推理 token` : ''}
                      </span>
                      {run.error ? <span className="text-critical">{run.error}</span> : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <Modal
        open={editing}
        onClose={() => setEditing(false)}
        title="编辑创意"
        description="内容改动后原研判结论可能过时，界面上会给出提示，建议重新研判。"
        size="lg"
      >
        <IdeaForm
          tagOptions={settings.tags}
          submitting={savingEdit}
          submitLabel="保存修改"
          initial={{
            title: idea.title,
            summary: idea.summary,
            body: idea.body,
            hypothesis: idea.hypothesis,
            audience: idea.audience,
            successMetric: idea.successMetric,
            tags: idea.tags,
          }}
          onSubmit={(value) => void saveEdit(value)}
          onCancel={() => setEditing(false)}
        />
      </Modal>

      <p className="text-[11px] text-fg-subtle">
        当前状态：{IDEA_STATUS_LABELS[idea.status]}。流转按钮由状态机决定 ——
        界面上的按钮与服务端的校验读的是同一份定义。
      </p>
    </PageContainer>
  );
}
